import { EVAL_WINNERS, type EvalWinner } from "contracts/enums/evals";
import { createSignal, For, Show } from "solid-js";
import {
  HUMAN_VERDICT_STATE,
  HUMAN_VERDICT_STATE_COPY,
  type HumanVerdictState,
} from "../../enums/evals";
import { adminGateProps } from "../../store/admin";
import { api } from "../../store/api";
import { createAsyncAction } from "../../ui/asyncAction";
import { FormError } from "../../ui/FormError";
import { PanelSection } from "../playground/PanelSection";
import { type VariantLabels, winnerLabel } from "./comparisonState";

/**
 * The analyst's verdict, which outranks the model's. The backend accepts one
 * per comparison and only once the pair is judgeable; both conditions are read
 * off the comparison itself (`humanVerdictState`), so the control is simply not
 * live when a write would be refused.
 *
 * A rejection that still happens — two analysts submitting at once — is shown
 * as the backend's own sentence, and the comparison is reloaded so the standing
 * verdict on screen is the one that was actually stored.
 */
export function HumanVerdictForm(props: {
  comparisonId: string;
  state: HumanVerdictState;
  labels: VariantLabels;
  onRecorded: () => void;
}) {
  const [winner, setWinner] = createSignal<EvalWinner | null>(null);
  const [rationale, setRationale] = createSignal("");
  const action = createAsyncAction();

  const submit = (event: Event) => {
    event.preventDefault();
    const chosen = winner();
    if (!chosen || action.busy()) return;
    void action.run(async () => {
      try {
        await api.evals.recordHumanJudgment(props.comparisonId, {
          winner: chosen,
          rationale: rationale().trim() || null,
        });
        props.onRecorded();
      } catch (cause) {
        props.onRecorded();
        throw cause;
      }
    });
  };

  return (
    <PanelSection title="Your verdict">
      <p class="landing-sub">{HUMAN_VERDICT_STATE_COPY[props.state]}</p>
      <Show when={props.state === HUMAN_VERDICT_STATE.OPEN}>
        <form onSubmit={submit}>
          <div>
            <For each={EVAL_WINNERS}>
              {(option) => (
                <button
                  type="button"
                  class={winner() === option ? "btn btn-primary" : "btn"}
                  onClick={() => setWinner(option)}
                >
                  {winnerLabel(option, props.labels)}
                </button>
              )}
            </For>
          </div>
          <input
            class="editor-input"
            placeholder="Why (optional)"
            value={rationale()}
            onInput={(event) => setRationale(event.currentTarget.value)}
          />
          <FormError message={action.error()} />
          <button
            class="btn btn-primary"
            type="submit"
            {...adminGateProps(
              action.busy() || winner() === null,
              "record a verdict",
            )}
          >
            {action.busy() ? "Recording…" : "Record verdict"}
          </button>
        </form>
      </Show>
    </PanelSection>
  );
}
